import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { coreSearchApiUrlPrefix, accountServiceApiUrlPrefix } from 'library-@/config/constants';
import type {
    ApiTranslationSyncRequest,
    ApiTranslationSyncResponse,
    TranslationSync
} from './interface/translation-sync.interface';
import type {
    ApiTranslationOriginalRequest,
    ApiTranslationOriginalResponse,
    TranslationOriginal
} from './interface/translation-original.interface';
import type { ApiGetTaskIdRequest, GetTaskId } from '@/infrastructure/api-functions/interface/get-task-id.interface';
import {
    GetHistoryList,
    ApiGetHistoryListRequest,
    ApiGetHistoryListResponse
} from '@/infrastructure/api-functions/interface/get-history-list.interface';
import { PutHistory, ApiPutHistoryRequest } from '@/infrastructure/api-functions/interface/put-history.interface';
import {
    GetHistoryAsyncStatusList,
    ApiGetHistoryAsyncStatusListRequest,
    ApiGetHistoryAsyncStatusItemResponse
} from '@/infrastructure/api-functions/interface/get-history-async-status-list.interface';
import {
    GetHistoryDetail,
    ApiGetHistoryDetailResponse
} from '@/infrastructure/api-functions/interface/get-history-detail.interface';
import { PutHistoryRead } from '@/infrastructure/api-functions/interface/put-history-read.interface';
import type { SSEStreamService } from '@patsnap-utils/sse';
import { SSEStreamServiceImpl } from '@patsnap-utils/sse';
import psHttp from '@patsnap/common-ps-http';
import { getAsyncToken } from 'library-@/token-base';
import { getCurrentLanguage } from 'library-@/utils/getCurrentLanguage';
import type {
    ApiTransferQueryRequest,
    ApiTransferQueryResponse,
    TransferQuery
} from './interface/transfer-query.interface';
import type {
    ApiGetTechTopicRequest,
    ApiGetTechTopicResponse,
    GetTechTopic
} from './interface/get-tech-topic.interface';
import type {
    ApiAddTranslationAsyncTaskRequest,
    AddTranslationAsyncTask
} from './interface/add-translation-async-task.interface';
import type {
    ApiGetTranslationAsyncStatusResponse,
    GetTranslationAsyncStatus
} from './interface/get-translation-async-status.interface';
import type { ApiPutTranslationTextRequest, PutTranslationText } from './interface/put-translation-text.interface';
import type { ApiExportTranslationRequest, ExportTranslation } from './interface/export-translation.interface';
import type {
    ApiCheckSensitiveWordRequest,
    ApiCheckSensitiveWordResponse,
    CheckSensitiveWord
} from './interface/check-sensitive-word.interface';

import type {
    GetTechMeans,
    ApiGetTechMeansRequest,
    ApiGetTechMeansResponse
} from '@/infrastructure/api-functions/interface/get-tech-means.interface';
import type {
    PutTechMeans,
    ApiPutTechMeansRequest,
    ApiPutTechMeansResponse
} from '@/infrastructure/api-functions/interface/put-tech-means.interface';
import type {
    GetDisclosureTitle,
    ApiGetDisclosureTitleRequest
} from '@/infrastructure/api-functions/interface/get-disclosure-title.interface';
import type {
    GetDisclosureContent,
    ApiGetDisclosureContentRequest,
    ApiGetDisclosureContentResponse
} from '@/infrastructure/api-functions/interface/get-disclosure-content.interface';
import type {
    PutDisclosureSetting,
    ApiPutDisclosureSettingRequest,
    ApiPutDisclosureSettingResponse
} from '@/infrastructure/api-functions/interface/put-disclosure-setting.interface';
import type {
    ExportDisclosure,
    ApiExportDisclosureRequest
} from '@/infrastructure/api-functions/interface/export-disclosure.interface';

export const CoreSearchApiService = Symbol('CoreSearchApiService');

export type CoreSearchApiService = PsHttpService;

@injectable()
export class CoreSearchApiServiceImpl extends PsHttpService
    implements CheckSensitiveWord,
        TransferQuery,
        GetTaskId,
        GetHistoryList,
        PutHistory,
        GetHistoryAsyncStatusList,
        GetHistoryDetail,
        PutHistoryRead,
        GetTechTopic,
        TranslationSync,
        TranslationOriginal,
        AddTranslationAsyncTask,
        GetTranslationAsyncStatus,
        PutTranslationText,
        ExportTranslation,
        GetTechMeans,
        PutTechMeans,
        GetDisclosureTitle,
        GetDisclosureContent,
        PutDisclosureSetting,
        ExportDisclosure {
    get options() {
        return {
            baseURL: coreSearchApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    /* 
    * 该请求用于SSE接口刷新token使用，直接使用psHttp调用SSE接口，仍会发生长连接的问题
    */
    private _refreshToken() {
        return psHttp({
            url: `${accountServiceApiUrlPrefix}/account-info/extra-data`,
            method: 'get',
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    async checkSensitiveWord(data: ApiCheckSensitiveWordRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiCheckSensitiveWordResponse>>({
            url: '/ai-search/sensitive-word',
            data,
            cache: true,
            transformCode: true,
        });
        return res.data;
    }

    async transferQuery(data: ApiTransferQueryRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiTransferQueryResponse>>({
            url: '/transfer-query',
            data,
            transformCode: true,
        });
        return res.data;
    }

    // 历史相关接口
    async getTaskId(params: ApiGetTaskIdRequest) {
        const res = await this.get<PatSnapCommonResponseData<string>>({
            url: '/ai/history/task-id',
            params,
            transformCode: true,
        });
        return res.data;
    }

    async getHistoryList(params: ApiGetHistoryListRequest) {
        const res = await this.get<PatSnapCommonResponseData<ApiGetHistoryListResponse>>({
            url: '/ai/history',
            params,
            transformCode: true,
        });
        return res.data;
    }

    async putHistory(data: ApiPutHistoryRequest) {
        const res = await this.put<PatSnapCommonResponseData<void>>({
            url: '/ai/history',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getHistoryAsyncStatusList(params: ApiGetHistoryAsyncStatusListRequest) {
        const res = await this.get<PatSnapCommonResponseData<ApiGetHistoryAsyncStatusItemResponse[]>>({
            url: '/ai/history/async-status',
            params,
            transformCode: true,
        });
        return res.data;
    }

    async getHistoryDetail(taskId: string) {
        const res = await this.get<PatSnapCommonResponseData<ApiGetHistoryDetailResponse>>({
            url: `/ai/history/${ taskId }`,
            transformCode: true,
        });
        return res.data;
    }

    async putHistoryRead(taskId: string) {
        const res = await this.put<PatSnapCommonResponseData<void>>({
            url: `/ai/history/${ taskId }/read`,
            transformCode: true,
        });
        return res.data;
    }

    // ai翻译相关接口
    async getTechTopic(data: ApiGetTechTopicRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiGetTechTopicResponse>>({
            url: '/ai-translation/tech-topic',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getSuggestions(data: any) {
        const res = await this.post<PatSnapCommonResponseData<any>>({
            url: '/ai-translation/keyword',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async addTranslationAsyncTask(data: ApiAddTranslationAsyncTaskRequest) {
        const res = await this.post<PatSnapCommonResponseData<void>>({
            url: '/ai-translation/translation/async',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getTranslationAsyncStatus(taskId: string) {
        const res = await this.get<PatSnapCommonResponseData<ApiGetTranslationAsyncStatusResponse>>({
            url: `/ai-translation/translation/async/${ taskId }/status`,
            transformCode: true,
        });
        return res.data;
    }

    async putTranslationText(data: ApiPutTranslationTextRequest) {
        const res = await this.put<PatSnapCommonResponseData<void>>({
            url: '/ai-translation/translation',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async exportTranslation(data: ApiExportTranslationRequest) {
        const res = await this.post<PatSnapCommonResponseData<string>>({
            url: '/ai-translation/export',
            data,
            transformCode: true,
        });
        return res.data;
    }

    translationSync(data: ApiTranslationSyncRequest): SSEStreamService<ApiTranslationSyncResponse> {
        return new SSEStreamServiceImpl({
            url: `${ coreSearchApiUrlPrefix }/ai-translation/translation/sync`,
            data,
            headerOptions: {
                asyncToken: getAsyncToken,
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
            refreshToken: this._refreshToken as unknown as () => Promise<void>,
        });
    }

    translationOriginal(data: ApiTranslationOriginalRequest): SSEStreamService<ApiTranslationOriginalResponse> {
        return new SSEStreamServiceImpl({
            url: `${ coreSearchApiUrlPrefix }/ai-translation/original`,
            data,
            headerOptions: {
                asyncToken: getAsyncToken,
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
            refreshToken: this._refreshToken as unknown as () => Promise<void>,
            fetchOptions: {
                method: 'PUT',
            },
        });
    }

    async getTechMeans(data: ApiGetTechMeansRequest) {
        const res = await this.post<PatSnapCommonResponseData<ApiGetTechMeansResponse>>({
            url: '/patent-disclosure/tech-means',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async putTechMeans(data: ApiPutTechMeansRequest) {
        const res = await this.put<PatSnapCommonResponseData<ApiPutTechMeansResponse>>({
            url: '/patent-disclosure/tech-means',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async getDisclosureTitle(data: ApiGetDisclosureTitleRequest) {
        const res = await this.post<PatSnapCommonResponseData<string>>({
            url: '/patent-disclosure/title',
            data,
            transformCode: true,
        });
        return res.data;
    }

    getDisclosureContent(data: ApiGetDisclosureContentRequest): SSEStreamService<ApiGetDisclosureContentResponse> {
        return new SSEStreamServiceImpl({
            url: `${ coreSearchApiUrlPrefix }/patent-disclosure/content`,
            data,
            headerOptions: {
                asyncToken: getAsyncToken,
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
            refreshToken: this._refreshToken as unknown as () => Promise<void>,
        });
    }

    async putDisclosureSetting(data: ApiPutDisclosureSettingRequest) {
        const res = await this.put<PatSnapCommonResponseData<ApiPutDisclosureSettingResponse>>({
            url: '/patent-disclosure/setting',
            data,
            transformCode: true,
        });
        return res.data;
    }

    async exportDisclosure(data: ApiExportDisclosureRequest) {
        const res = await this.post<PatSnapCommonResponseData<string>>({
            url: '/patent-disclosure/export',
            data,
            transformCode: true,
        });
        return res.data;
    }
}
